import { Dayjs } from 'dayjs'
import { ReactNode } from 'react'

import {
    useIsPredictedWaitingListEnabled,
    usePractitionerPredictedWaitingList,
} from '~/app/pages/SurgeonScheduler/TablesLayout/Practitioners/usePractitionerPredictedWaitingList'
import { AstraBadge, AstraChip, AstraParagraph, AstraSkeletonLoader, AstraTooltip, DATE_TEMPLATES, format, getToday } from '~/astra'
import { useOpenDialog } from '~/components/DialogRouter/useDialogRouter'
import { PractitionerChipWithTooltip } from '~/components/PractitionerChip'
import { useCurrentWaitingListByPractitioner } from '~/hooks/useCurrentWaitingListByPractitioner'
import { useStore } from '~/store/store'
import { Practitioner, usePractitioner } from '~/storm'
import { getPractitionerFullname } from '~/utils/dips'

import { WaitingListCallout } from '../../SurgeonScheduler/shared'

const SkeletonRow = () => (
    <span className="h-6 w-full">
        <AstraSkeletonLoader />
    </span>
)
const InformationRow = ({ children, isLoading }: { children: ReactNode; isLoading: boolean }) =>
    isLoading ? <SkeletonRow /> : <div className="flex justify-between">{children}</div>

const WaitingListAction = ({ onClick, title, subtitle }: { onClick: () => void; title: string; subtitle: string }) => (
    <span className="flex cursor-pointer items-center hover:underline" onClick={onClick}>
        <AstraParagraph weight="medium" variant="body-small">
            {title}
            <span className="font-hero-regular text-muted"> - {subtitle}</span>
        </AstraParagraph>
    </span>
)

const CurrentWaitingListContent = ({ practitioner }: { practitioner: Practitioner }) => {
    const openPractitionerPopover = useOpenDialog('practitioner')
    const { isError, isLoading, isWaitingListEmpty, unScheduledSurgeriesByPractitionerCount, clearedSurgeriesByPractitionerCount } =
        useCurrentWaitingListByPractitioner(practitioner)

    const badgeText = isWaitingListEmpty ? '-' : `${unScheduledSurgeriesByPractitionerCount} (${clearedSurgeriesByPractitionerCount})`

    return isError ? (
        <WaitingListCallout type="error" />
    ) : (
        <InformationRow isLoading={isLoading}>
            <WaitingListAction
                onClick={() => openPractitionerPopover({ practitioner })}
                title={'I dag'}
                subtitle={format(getToday(), DATE_TEMPLATES.norwegianDate)}
            />
            <AstraTooltip tooltipContent={`${unScheduledSurgeriesByPractitionerCount} pasienter (${clearedSurgeriesByPractitionerCount} klarerte)`}>
                <AstraBadge text={badgeText} />
            </AstraTooltip>
        </InformationRow>
    )
}

const PredictedWaitingListContent = ({ practitioner, date }: { practitioner: Practitioner; date: Dayjs }) => {
    const predictedWaitingList = usePractitionerPredictedWaitingList(practitioner)

    const hasNoData = predictedWaitingList.currentUnscheduledWaitingList === 0
    const totalEndOfMonth = predictedWaitingList.waitingListEndOfMonth
    const color = totalEndOfMonth < 0 ? 'critical' : 'default'

    const badgeText = hasNoData ? '-' : `${totalEndOfMonth}`
    const openPractitionerPopover = useOpenDialog('practitioner')
    return predictedWaitingList.isError ? (
        <WaitingListCallout type="error" />
    ) : (
        <InformationRow isLoading={predictedWaitingList.isLoading}>
            <WaitingListAction
                onClick={() => openPractitionerPopover({ practitioner, defaultTab: 'predictedWaitingList' })}
                title={'Predikert'}
                subtitle={format(date, DATE_TEMPLATES.monthYear)}
            />
            <AstraTooltip tooltipContent={`${predictedWaitingList.waitingListEndOfMonth} pasienter på venteliste ved slutten av perioden`}>
                <AstraBadge text={badgeText} color={color} />
            </AstraTooltip>
        </InformationRow>
    )
}

type WaitingListContentProps = {
    practitioner: Practitioner
}

const WaitingListContent = ({ practitioner }: WaitingListContentProps) => {
    const selectedDate = useStore(state => state.appFilters.selectedDate)

    const { enabled: predictedWaitingListEnabled } = useIsPredictedWaitingListEnabled()
    return (
        <div className="flex flex-col gap-1.5">
            <AstraParagraph className="text-muted">Venteliste for operasjon</AstraParagraph>
            <CurrentWaitingListContent practitioner={practitioner} />
            {predictedWaitingListEnabled && <PredictedWaitingListContent practitioner={practitioner} date={selectedDate} />}
        </div>
    )
}

const PractitionerPreviewHeader = ({ practitioner }: { practitioner: Practitioner }) => {
    const openPractitionerPopover = useOpenDialog('practitioner')
    const practitionerPopulated = usePractitioner({
        where: {
            id: practitioner.id,
        },
        include: {
            departments: true,
            dipsResource: true,
        },
    })
    const practitionerName = getPractitionerFullname(practitionerPopulated)

    return (
        <div className="flex flex-col gap-x-2 divide-x border-b border-subtle pb-3">
            <div className="flex flex-col gap-y-1.5 px-4">
                <AstraParagraph
                    variant="body-large"
                    weight="strong"
                    className="cursor-pointer text-default hover:underline"
                    onClick={() => openPractitionerPopover({ practitioner })}
                >
                    {practitionerName}
                </AstraParagraph>
                <div className="flex items-center gap-1.5">
                    <PractitionerChipWithTooltip shortName={practitioner.short_name} fullName={practitionerName} />
                    {practitionerPopulated?.departments.map(department => <AstraChip key={department.uuid} label={department.name} />)}
                </div>
            </div>
        </div>
    )
}

export type PractitionerPreviewContentProps = WaitingListContentProps & {
    children?: ReactNode
}
export const PractitionerPreviewContent = ({ practitioner, children, ...waitingListProps }: PractitionerPreviewContentProps) => (
    <div className="flex w-64 flex-col py-4">
        <PractitionerPreviewHeader practitioner={practitioner} />
        <div className="px-4 pt-3">
            <WaitingListContent practitioner={practitioner} {...waitingListProps} />
            {children}
        </div>
    </div>
)
